import React, { useEffect, useState } from "react";
import { DescriptionText, FlexContainer, H1Text, H2Text, Spinner } from "../../design";
import TransactionModal from "../../components/modal/transaction";
import SuccessModal from "../../components/modal/success";
import { addFundingSourceLink, getFundingSourceById, getPlaidCreateLinkToken } from "../../api";
import { MESSAGES, PAYMENT_BUTTON_STATUSES, SWIREPAY_URL } from "../../appconstants";
import BaseSplitPage from "../../components/splitpage";
import Header from "./smallscreen/header";
import SmallScreenFooter from "../../components/footer";
import Support from "../../components/support";
import FundingLink from "../../components/fundingLink/FundingLink";
import FundingsourceLinkTabs from "../../components/fundingsourcelinktabs";
import PlaidLinkOpener from "./components/PlaidLinkOpener";

type Props = {
  history: {
    push: Function,
  },
}

const FundingsourceLink = (props: Props) => {

  const { match: { params: { fundingSourcelinkId } }, location: { pathname, search } } = props;
  const [isTransactionModalOpen, setTransactionModalOpen] = useState(false);
  const [isTransactionSuccessModalOpen, setTransactionSuccessModalOpen] = useState(false);
  const [payment, setPayment] = useState(null);
  const [transferType, setTransferType] = useState(null);
  const [issuerType, setIssuerType] = useState(null);
  const [errorMessage, setErrorMessage] = useState();
  const [bankImageUrl, setBankImageUrl] = useState(null);
  const [partnerImageUrl, setPartnerImageUrl] = useState(null);
  const [isShowSpinner, setShowSpinner] = useState(false);
  const [plaidAccessToken, setPlaidAccessToken] = useState(null);
  const [openPlaidModal, setOpenPlaidModal] = useState(false);
  const [parameter, setParameter] = useState(null);
  const [plaidVerified, setPlaidVerified] = useState(false);

  useEffect(() => {
    getPaymentButtonDataWithoutSearchValue();
    getAccessPlaidToken();
  }, [fundingSourcelinkId]);

  const getPaymentButtonDataWithoutSearchValue = async () => {
    setShowSpinner(true);
    try {
      const value = await getFundingSourceById(fundingSourcelinkId);
      if (value) {
        setPayment(value);
        setIssuerType(value.account && value.account.issuer && value.account.issuer.name);
        setTransferType(value.transferType && value.transferType.value);
        setBankImageUrl(value.account && value.account.bankImageUrl);
        setPartnerImageUrl(value.account && value.account.partnerImageUrl);
      }
    } catch (e) {
      props.history.push(`${pathname}/error`);
    } finally {
      setShowSpinner(false);
    }
  };

  const getAccessPlaidToken = async () => {
      setShowSpinner(true);
      try {
          const value = await getPlaidCreateLinkToken(fundingSourcelinkId);
          if (value?.token) {
              setPlaidAccessToken(value?.token);
              setShowSpinner(false);
          }
      } catch (e) {
          error(e.response);
      } finally {
        setShowSpinner(false);
      }
  };

  const error = reason => {
    setErrorMessage(null);
    const { data: { message, messageCode }, status } = reason;
    if (messageCode === 5102) {
      props.history.push(`${pathname}/error`);
      return;
    }
    setErrorMessage(message);
    if (status === 500) {
      setErrorMessage(MESSAGES.PAYMENT_LINK.ERROR);
    }
    setTransactionModalOpen(false);
    const errorEl = document.getElementById("error");
    if (null !== errorEl) { errorEl.scrollIntoView(); }
  };

  const addFundingSource = async (data) => {
    const { paymentMethod, beneType, isDefault } = data;
    let issuerBank;
    if (beneType === 'VPA') {
      issuerBank = {
        vpa: data.vpa,
        countryCode: payment.account.country.alpha2,
        beneType,
      }
     } else {
      issuerBank = {
        accountNumber: paymentMethod.bank.accountNumber,
        accountType: paymentMethod.bank.accountType,
        businessType : paymentMethod.bank.businessType,
        name: data.name,
        bankName: paymentMethod.bank.bankName,
        countryCode:payment?.contact?.address?.country?.alpha2 ,
        accountHolderName: paymentMethod.bank.accountHolderName,
        beneType,
      }
      }
    const params = payment?.contact?.address?.country?.alpha2 === 'US' ? {
      contactGid: payment.contact.gid,
      issuerBank: {
            businessType: issuerBank?.businessType,
            beneType: issuerBank.beneType,
            countryCode: issuerBank.countryCode,
      },
      isDefault,
    } : {
      contactGid: payment.contact.gid,
      issuerBank,
      isDefault,
    }
    if (beneType !== 'VPA') {
      if (payment.account.country.alpha2 === 'IN') {
        params.issuerBank.routingNumber = data.routingNumber;
      } else {
        params.issuerBank.routingNumber = data.routingNumber;
      }
    }
    if (payment?.contact?.address?.country?.alpha2 === 'US') {
        setParameter(params);
        setOpenPlaidModal(true);
    } else {
      chargeById(params, null);
    }
  };

  const chargeById = async (data, plaidPublicToken) => {
    setTransactionModalOpen(true);
    try {
      const value = await addFundingSourceLink(fundingSourcelinkId, {...data, issuerBank: {
        ...data.issuerBank,
        plaidPublicToken: plaidPublicToken,
      },});
      if (value) {
        const { nextActionUrl, redirectUri } = value;
        if (nextActionUrl) {
          window.location = nextActionUrl;
          return;
        }
        setTransactionModalOpen(false);
        setTransactionSuccessModalOpen(true);
        setTimeout(function () {
          const redirectUrl = redirectUri ? redirectUri : SWIREPAY_URL;
          const urlPath = redirectUrl.includes("?");
          if (urlPath) {
            window.location = `${redirectUrl}&sp-fundingsource-link=${fundingSourcelinkId}`;
          } else if (redirectUrl && !urlPath) {
            window.location = `${redirectUrl}?sp-fundingsource-link=${fundingSourcelinkId}`;
          }
        }, 5000);
      }
    } catch (reason) {
      error(reason);
    }
  };

  const tabPanels = [
    {
      children: <FundingLink
        errorMsg={errorMessage}
        errorHeader={null}
        submit={addFundingSource}
        preFillCustomer={payment && payment.contact}
        customerDisabled={true}
        transferType={transferType}
        issuerType={issuerType}
        headerText={"RECEIVE FUNDS BY BANK TRANSFER"}
        buttonText={!plaidVerified && payment?.contact?.address?.country?.alpha2 === 'US' ? "VERIFY VIA PLAID" : "SUBMIT"}
        customerValidation={true}
        alpha2={payment && payment?.contact && payment?.contact?.address?.country?.alpha2}
        buttonDisabled={!plaidAccessToken}
      />
    }
  ]

  return (
    <BaseSplitPage
    errorHeader={null}
    errorMsg={errorMessage}
    left={
      <>
        <FlexContainer
          flexDirection="column"
          position="relative"
          display="block"
          height="100%"
        >
          <div>
            {
              (payment && payment.account && payment.account.largeLogo) && (
                <img src={payment.account.largeLogo} style={{ width: "125px" }} alt="" />
              )
            }
            <H1Text>
              FUNDING SOURCE LINK
            </H1Text>
            <H2Text>
              TRANSFER FROM
            </H2Text>
            <DescriptionText>
              {payment && payment.account && payment.account.name}
            </DescriptionText>
          </div>
          <div style={{ position: "sticky", top: "85%" }}>
            <Support email={payment && payment.support && payment.support.email}
              partnerImageUrl={partnerImageUrl}
              bankImageUrl={bankImageUrl}
              whiteLabel={payment && payment.account && payment.account.whiteLabel}
              phone={payment && payment.support && payment.support.phone} />
          </div>
        </FlexContainer>
      </>
    } right={
      <>
        <Header 
          payment={payment} 
          bankImageUrl={bankImageUrl}
          cancelUrlLink={null} 
        />
        <FundingsourceLinkTabs
          name={fundingSourcelinkId}
          paymentTypes={(payment && payment.account && payment.account.currency && payment.account.currency.countryAlpha2)}
          tabs={tabPanels}
          tabPanels={tabPanels}
          accntName={payment && payment.account && payment.account.name}
        />
        <SmallScreenFooter
          partnerImageUrl={partnerImageUrl}
          bankImageUrl={bankImageUrl}
          whiteLabel={payment && payment.account && payment.account.whiteLabel}
        />
        {plaidAccessToken && openPlaidModal && (
          <PlaidLinkOpener
            token={plaidAccessToken}
            onClose={() => window.location = window.location.href}
            // eslint-disable-next-line camelcase
            onSuccess={(public_token, metadata) => {
              console.log('Plaid success', public_token, metadata);
              setPlaidVerified(true);
              chargeById(parameter, public_token);
            }}
          />
        )}
        <TransactionModal body={"Creating Funding Source"} header={null} isOpen={isTransactionModalOpen} />
        <SuccessModal isOpen={isTransactionSuccessModalOpen} successMsg={"Funding Source Created"} />
        <Spinner isOpen={isShowSpinner} />
      </>
    } />
  )
};

export default FundingsourceLink;
